import { montarPayloadQr } from '@portal-leo/shared';

/**
 * Folha A4 com 24 etiquetas (3 colunas × 8 linhas de 63,5 × 33,9 mm), formato
 * comum de etiqueta adesiva no Brasil e igualmente imprimível em papel comum
 * para recorte.
 */
export const LAYOUT_A4 = {
  colunas: 3,
  linhas: 8,
  larguraEtiqueta: 63.5,
  alturaEtiqueta: 33.9,
  margemEsquerda: 9.75,
  margemTopo: 12.9,
} as const;

export const ETIQUETAS_POR_FOLHA = LAYOUT_A4.colunas * LAYOUT_A4.linhas;

/**
 * Uma etiqueta = uma peça física.
 *
 * O `codigo` aqui é o da peça (`00010-3`), não o do produto: é isso que faz o
 * leitor saber qual camiseta saiu.
 */
export interface Etiqueta {
  codigo: string;
  nome: string;
  preco: string;
}

export interface PosicaoEtiqueta {
  pagina: number;
  linha: number;
  coluna: number;
  /** Canto superior esquerdo da etiqueta, em milímetros. */
  x: number;
  y: number;
}

/**
 * Onde cada etiqueta cai na folha.
 *
 * `posicaoInicial` (1 a 24) desloca o preenchimento para reaproveitar uma folha
 * de etiquetas já parcialmente usada — sem isso, cada peça cadastrada depois
 * custaria uma folha nova.
 */
export function calcularPosicoes(quantidade: number, posicaoInicial: number): PosicaoEtiqueta[] {
  if (!Number.isInteger(quantidade) || quantidade < 1) {
    throw new Error('Selecione ao menos uma etiqueta para imprimir');
  }

  if (
    !Number.isInteger(posicaoInicial) ||
    posicaoInicial < 1 ||
    posicaoInicial > ETIQUETAS_POR_FOLHA
  ) {
    throw new Error(`Posição inicial deve estar entre 1 e ${ETIQUETAS_POR_FOLHA}`);
  }

  const posicoes: PosicaoEtiqueta[] = [];

  for (let i = 0; i < quantidade; i++) {
    const absoluta = posicaoInicial - 1 + i;
    const pagina = Math.floor(absoluta / ETIQUETAS_POR_FOLHA);
    const slot = absoluta % ETIQUETAS_POR_FOLHA;
    const linha = Math.floor(slot / LAYOUT_A4.colunas);
    const coluna = slot % LAYOUT_A4.colunas;

    posicoes.push({
      pagina,
      linha,
      coluna,
      x: LAYOUT_A4.margemEsquerda + coluna * LAYOUT_A4.larguraEtiqueta,
      y: LAYOUT_A4.margemTopo + linha * LAYOUT_A4.alturaEtiqueta,
    });
  }

  return posicoes;
}

const formatarPreco = (preco: string) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    Number.parseFloat(preco) || 0
  );

/**
 * Monta o PDF e entrega ao navegador.
 *
 * `jspdf` e `qrcode` entram por import dinâmico para não pesar o bundle
 * inicial do PDV, seguindo o que a exportação de passeios já faz.
 */
export async function gerarEtiquetasPdf(
  etiquetas: Etiqueta[],
  posicaoInicial: number
): Promise<void> {
  const posicoes = calcularPosicoes(etiquetas.length, posicaoInicial);

  const [{ jsPDF }, QRCode] = await Promise.all([import('jspdf'), import('qrcode')]);

  const doc = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });

  const PADDING = 3;
  const LADO_QR = LAYOUT_A4.alturaEtiqueta - PADDING * 2;
  const xTexto = (x: number) => x + PADDING + LADO_QR + 2.5;
  const larguraTexto = LAYOUT_A4.larguraEtiqueta - PADDING * 2 - LADO_QR - 2.5;

  let paginaAtual = 0;

  for (const [indice, etiqueta] of etiquetas.entries()) {
    const posicao = posicoes[indice]!;

    if (posicao.pagina > paginaAtual) {
      doc.addPage();
      paginaAtual = posicao.pagina;
    }

    const dataUrl = await QRCode.toDataURL(montarPayloadQr(etiqueta.codigo), {
      margin: 0,
      errorCorrectionLevel: 'M',
      width: 256,
    });

    doc.addImage(dataUrl, 'PNG', posicao.x + PADDING, posicao.y + PADDING, LADO_QR, LADO_QR);

    doc.setFontSize(8);
    doc.setTextColor(0);
    const nome = doc.splitTextToSize(etiqueta.nome, larguraTexto).slice(0, 2) as string[];
    doc.text(nome, xTexto(posicao.x), posicao.y + PADDING + 3);

    doc.setFontSize(11);
    doc.text(formatarPreco(etiqueta.preco), xTexto(posicao.x), posicao.y + PADDING + 14);

    doc.setFontSize(9);
    doc.setTextColor(90);
    doc.text(etiqueta.codigo, xTexto(posicao.x), posicao.y + PADDING + 21);
  }

  const hoje = new Date().toISOString().split('T')[0];
  doc.save(`etiquetas-${hoje}.pdf`);
}
